import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
  BLOCKS,
  MARKS,
  Document as RichTextDocument,
} from "@contentful/rich-text-types";
import cx from "classnames";
import { readFragment, ResultOf } from "gql.tada";
import { useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { RoutingSectionById } from "@queries/entries/RoutingSectionById";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { Button } from "@components/shared/core/Button";
import { Link } from "@components/shared/core/Link";
import { LinkFragment } from "@queries/fragments/LinkFragment";

type Entry = ResultOf<typeof RoutingSectionById>;

export const GetStartedRoutingSection = ({
  entry: { routingSection: entry },
}: {
  entry: Entry;
}) => {
  const link = readFragment(LinkFragment, entry?.cta);

  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const value = useTransform(scrollYProgress, [0, 0.5, 1], [-1, 0, 0]);

  if (!entry) return null;

  return (
    <div
      ref={containerRef}
      className={cx('px-[18px] py-[60px] dsk:px-8 dsk:py-[38px] relative', entry?.variant !== 'image-auto-height' && 'h-[694px] dsk:h-[850px]')}
    >
      <section
        className={cx(
          "bg-cover bg-center bg-no-repeat rounded-xl overflow-hidden relative flex items-center justify-center",
          {
            "flex-col": entry.isVertical,
            "flex-row": !entry.isVertical,
          },
          entry?.variant !== 'image-auto-height' && 'h-full'
        )}
      >
        {entry.backgroundImage?.url && (
          <img
            className={cx('inset-0 size-full', entry?.variant !== 'image-auto-height' && 'absolute object-cover brightness-60')}
            src={entry.backgroundImage.url}
            alt=""
          />
        )}
        {entry?.variant !== 'image-auto-height' &&
          <div
            className={cx("flex dsk:px-[98px]", {
              "gap-[38px] dsk:gap-[46px] items-center px-8 flex-col":
                entry.isVertical,
              "p-6 justify-between h-full flex-col dsk:flex-row dsk:items-center dsk:w-full":
                !entry.isVertical,
            })}
          >
            <div
              className={cx("text-brand-cream", {
                "text-display text-center": entry.isVertical,
                "text-heading-1 self-start dsk:self-auto": !entry.isVertical,
              })}
            >
              {entry.titleRichText?.json != null &&
                documentToReactComponents(
                  entry.titleRichText.json as RichTextDocument,
                  {
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (_, children) => (
                        <h2>
                          <AnimatedSplitLines value={value}>
                            {children}
                          </AnimatedSplitLines>
                        </h2>
                      ),
                    },
                    renderMark: {
                      [MARKS.ITALIC]: (text) => (
                        <em className="italic [font-synthesis:style]">{text}</em>
                      ),
                    },
                    // Honor soft line breaks (Shift+Enter) from the rich-text editor.
                    // AnimatedSplitLines handles <br/> tokens in its split pipeline.
                    renderText: (text) =>
                      text
                        .split("\n")
                        .flatMap((segment, i) =>
                          i === 0 ? [segment] : [<br key={i} />, segment]
                        ),
                  }
                )}
            </div>
            {link && (
              <Button
                asChild
                variant="light-on-blur"
                className={cx(
                  "w-max",
                  !entry.isVertical && "self-end dsk:self-auto"
                )}
              >
                <Link link={link}>{link.text}</Link>
              </Button>
            )}
          </div>
        }
      </section>
    </div>
  );
};
